import { MainLayout } from "@/components/MainLayout";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { usePortfolio } from "@/context/PortfolioContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useState, useEffect } from "react";
import {
  User,
  Palette,
  DollarSign,
  Moon,
  Sun,
  LogOut,
  Shield,
  Target,
  Gauge,
  Eye,
  Globe,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";

const SettingsPage = () => {
  const { user, logout, updatePreferences, isDemo } = useAuth();
  const { theme, toggleTheme, setTheme } = useTheme();
  const { currency, setCurrency } = usePortfolio();
  const { t, locale, changeLocale } = useI18n();
  const navigate = useNavigate();

  const ALL_CHANNELS = [
    { id: "my_deals", label: "My Deals" },
    { id: "defi", label: "DeFi" },
    { id: "stocks", label: "Stocks" },
    { id: "commodities", label: "Commodities" },
    { id: "web3_secondaries", label: "Web3 Secondaries" },
  ];
  const ALL_REGIONS = ["US", "Europe", "Asia-Pacific", "Emerging Markets"];

  const [riskTolerance, setRiskTolerance] = useState(user?.risk_tolerance || "moderate");
  const [preferredRegions, setPreferredRegions] = useState(user?.preferred_regions || ALL_REGIONS);
  const [visibleChannels, setVisibleChannels] = useState(user?.visible_channels || ALL_CHANNELS.map(c => c.id));

  useEffect(() => {
    if (user) {
      setRiskTolerance(user.risk_tolerance || "moderate");
      setPreferredRegions(user.preferred_regions || ALL_REGIONS);
      setVisibleChannels(user.visible_channels || ALL_CHANNELS.map(c => c.id));
    }
  }, [user]);

  const handleRiskChange = async (val) => {
    setRiskTolerance(val);
    try {
      await updatePreferences({ risk_tolerance: val });
      toast.success("Risk tolerance updated");
    } catch { toast.error("Failed to update"); }
  };

  const toggleRegion = async (region) => {
    const next = preferredRegions.includes(region)
      ? preferredRegions.filter(r => r !== region)
      : [...preferredRegions, region];
    setPreferredRegions(next);
    try {
      await updatePreferences({ preferred_regions: next });
      toast.success("Preferred regions updated");
    } catch { toast.error("Failed to update"); }
  };

  const toggleChannel = async (channel) => {
    const next = visibleChannels.includes(channel)
      ? visibleChannels.filter(c => c !== channel)
      : [...visibleChannels, channel];
    setVisibleChannels(next);
    try {
      await updatePreferences({ visible_channels: next });
      toast.success("Visible channels updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleCurrencyChange = async (newCurrency) => {
    setCurrency(newCurrency);
    try {
      await updatePreferences({ currency_preference: newCurrency });
      toast.success("Currency preference updated");
    } catch (error) {
      toast.error("Failed to update preferences");
    }
  };

  const handleThemeChange = async (newTheme) => {
    setTheme(newTheme);
    try {
      await updatePreferences({ theme_preference: newTheme });
    } catch (error) {
      console.error("Failed to save theme preference");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">
            {t("settings.title")}
          </h1>
          <p className="text-muted-foreground mt-1">{t("settings.subtitle")}</p>
        </div>

        {/* Profile Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              {t("settings.profile")}
            </CardTitle>
            <CardDescription>{t("settings.profileDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={user?.picture} alt={user?.name} />
                <AvatarFallback className="text-xl">{user?.name?.charAt(0) || "U"}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-lg font-semibold">{user?.name}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              {t("settings.appearance")}
            </CardTitle>
            <CardDescription>{t("settings.appearanceDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">{t("settings.theme")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("settings.themeDesc")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={theme === "light" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleThemeChange("light")}
                  data-testid="theme-light-btn"
                  className={theme === "light" ? "bg-primary text-primary-foreground" : ""}
                >
                  <Sun className="w-4 h-4 mr-1" />
                  {t("settings.light")}
                </Button>
                <Button
                  variant={theme === "dark" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleThemeChange("dark")}
                  data-testid="theme-dark-btn"
                  className={theme === "dark" ? "bg-primary text-primary-foreground" : ""}
                >
                  <Moon className="w-4 h-4 mr-1" />
                  {t("settings.dark")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Currency Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              {t("settings.displayCurrency")}
            </CardTitle>
            <CardDescription>{t("settings.displayCurrencyDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">{t("settings.currencyLabel")}</Label>
                <p className="text-sm text-muted-foreground">
                  {t("settings.currencyConvert")}
                </p>
              </div>
              <Select value={currency} onValueChange={handleCurrencyChange}>
                <SelectTrigger className="w-40" data-testid="settings-currency-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">
                    <span className="flex items-center gap-2">
                      <span>$</span> USD
                    </span>
                  </SelectItem>
                  <SelectItem value="EUR">
                    <span className="flex items-center gap-2">
                      <span>€</span> EUR
                    </span>
                  </SelectItem>
                  <SelectItem value="BTC">
                    <span className="flex items-center gap-2">
                      <span>₿</span> BTC
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Language Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              {t("settings.language")}
            </CardTitle>
            <CardDescription>{t("settings.languageDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">{t("nav.language")}</Label>
                <p className="text-sm text-muted-foreground">{t("settings.languageConvert")}</p>
              </div>
              <Select value={locale} onValueChange={changeLocale}>
                <SelectTrigger className="w-40" data-testid="settings-language-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="es">Español</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Investment Preferences Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-primary" />
              {t("settings.investPrefs")}
            </CardTitle>
            <CardDescription>{t("settings.investPrefsDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Risk Tolerance */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">{t("settings.riskTolerance")}</Label>
              </div>
              <p className="text-xs text-muted-foreground">{t("settings.riskToleranceDesc")}</p>
              <div className="flex gap-2" data-testid="risk-tolerance-selector">
                {["conservative", "moderate", "aggressive"].map((level) => (
                  <Button
                    key={level}
                    variant={riskTolerance === level ? "default" : "outline"}
                    size="sm"
                    onClick={() => !isDemo && handleRiskChange(level)}
                    disabled={isDemo}
                    data-testid={`risk-${level}`}
                    className="capitalize"
                  >
                    {t(`settings.risk_${level}`)}
                  </Button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Preferred Regions */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">{t("settings.preferredRegions")}</Label>
              </div>
              <p className="text-xs text-muted-foreground">{t("settings.preferredRegionsDesc")}</p>
              <div className="flex flex-wrap gap-3" data-testid="region-selector">
                {ALL_REGIONS.map((region) => (
                  <label key={region} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={preferredRegions.includes(region)}
                      onCheckedChange={() => !isDemo && toggleRegion(region)}
                      disabled={isDemo}
                      data-testid={`region-${region.replace(/\s+/g, "-").toLowerCase()}`}
                    />
                    <span className="text-sm">{region}</span>
                  </label>
                ))}
              </div>
            </div>

            <Separator />

            {/* Visible Channels */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <Label className="text-sm font-medium">{t("settings.visibleChannels")}</Label>
              </div>
              <p className="text-xs text-muted-foreground">{t("settings.visibleChannelsDesc")}</p>
              <div className="flex flex-wrap gap-3" data-testid="channel-selector">
                {ALL_CHANNELS.map((channel) => (
                  <label key={channel.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={visibleChannels.includes(channel.id)}
                      onCheckedChange={() => !isDemo && toggleChannel(channel.id)}
                      disabled={isDemo}
                      data-testid={`channel-${channel.id}`}
                    />
                    <span className="text-sm">{channel.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              {t("settings.security")}
            </CardTitle>
            <CardDescription>{t("settings.securityDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-secondary/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">{t("settings.googleConnected")}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("settings.googleAuth")}
                  </p>
                </div>
              </div>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-destructive">{t("settings.signOut")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("settings.signOutDesc")}
                </p>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleLogout}
                disabled={isDemo}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.logout")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* App Info */}
        <div className="text-center text-sm text-muted-foreground py-4">
          <p>{t("settings.version")}</p>
          <p className="mt-1">{t("settings.builtFor")}</p>
        </div>
      </div>
    </MainLayout>
  );
};

export default SettingsPage;
